import React, { useState, useCallback, useEffect } from 'react';
import DayHeader from '../../../../components/TimeSheet/DayCard/DayHeader';

import { Project, ProjectDayTimeTracked, Timesheet } from '../../types';
import DayTimeEntries from './DayTimeEntries/DayTimeEntries';
import { useTimeSheetDays } from './useTimeSheetDays';
import { CostCenter } from '../../../../__generated__/graphql';
import { toISODateOnly } from '../../../../../../utils/dateUtils';

type Props = {
  timesheet: Timesheet;
  projects: Project[];
  costCenters: ReadonlyArray<CostCenter>;
  showProjectDistribution: boolean;
};

export const ProjectTimesheetByDays: React.FC<Props> = ({
  timesheet,
  projects,
  costCenters,
  showProjectDistribution,
}) => {
  const days = useTimeSheetDays(timesheet, projects);
  const [currentTotalMinutesByDate, setCurrentTotalMinutesByDate] = useState<
    Record<string, number>
  >({});

  const handleTotalMinutesChange = useCallback(
    (dateId: string, minutes: number) => {
      setCurrentTotalMinutesByDate((prev) => ({
        ...prev,
        [dateId]: minutes,
      }));
    },
    [],
  );

  useEffect(() => {
    const dateIds = new Set(days.map((day) => toISODateOnly(day.day.toDate())));
    setCurrentTotalMinutesByDate((prev) => {
      const filtered: Record<string, number> = {};
      Object.keys(prev).forEach((dateId) => {
        if (dateIds.has(dateId)) {
          filtered[dateId] = prev[dateId];
        }
      });
      return filtered;
    });
  }, [days]);

  const callbacksByDateId = React.useMemo(() => {
    const callbacks: Record<string, (minutes: number) => void> = {};
    days.forEach((day) => {
      const dateId = toISODateOnly(day.day.toDate());
      callbacks[dateId] = (minutes: number) =>
        handleTotalMinutesChange(dateId, minutes);
    });
    return callbacks;
  }, [days, handleTotalMinutesChange]);

  return (
    <>
      {days.map((day) => {
        const dateId = toISODateOnly(day.day.toDate());
        const savedTotalMinutes = getTotalMinutes(day.timesTracked);
        const currentTotalMinutes = currentTotalMinutesByDate[dateId];
        const dayMinutes =
          currentTotalMinutes !== undefined
            ? currentTotalMinutes
            : savedTotalMinutes;

        return (
          <div key={day.day.toISOString()}>
            {/*The header above the card that shows the day, the comments and loom*/}
            <DayHeader
              dayInfo={day}
              trackedMinutes={day.flair__Time_Entries_Minutes__c}
              projectTimeEntriesMinutes={dayMinutes}
              absences={day.absences}
              holidays={day.holidays}
              calendarPeriods={day.calendarPeriods}
              commentsCount={day.commentsCount}
              showTrackedTime={showProjectDistribution}
              showProjectTrackedTime={true}
            />

            <DayTimeEntries
              day={day}
              timesheet={timesheet}
              costCenters={costCenters}
              showTimeEntries={showProjectDistribution}
              onTotalMinutesChange={callbacksByDateId[dateId]}
            />
          </div>
        );
      })}
    </>
  );
};

function getTotalMinutes(timesTracked: ProjectDayTimeTracked[]) {
  return timesTracked.reduce(
    (totalMinutes, timeTracked) => totalMinutes + (timeTracked.minutes || 0),
    0,
  );
}
